// packages/ops-platform-v3/src/frontend/expoams/ScanView.tsx
//
// Ported from StationJo's src/components/expoams/ScanView.tsx (Plan 09 Task 9).
// - Inventory writes (checkIn, checkOut, checkInReserved, checkOutReserved) go
//   through the ExpoAmsClient singleton from ./services/index.js (Plan 09 Task 15).
// - Types import swapped from '../../types/expoams' to './types.js' (siblings).
// - No Firebase Web SDK imports — ScanView's writes fan out through the service
//   layer.
// - `userName` already arrives as a prop from ExpoAmsRouter (sourced from
//   payload.user.email); no firebase.auth() lookup needed.

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import type {
  ExpoAmsAction,
  CheckOutPurpose,
  ParsedLocation,
  LogMap,
  InventoryWriteResponse,
} from './types.js';
import { expoamsClient } from './services/index.js';
import { InsufficientPrivilegeError, InvalidRequestError } from './services/errors.js';

export interface ScanViewProps {
  sheetIds: string[];
  sheetNames: Record<string, string>;
  selectedSheetId: string;
  onSelectSheet: (id: string) => void;
  userName: string;
  /** Fired after a successful check-in / check-out so cached inventory and
   *  history views for the sheet can be invalidated. */
  onTransactionComplete?: (sheetId: string) => void;
}

type ScanStep = 'select-action' | 'scan-location' | 'scan-items' | 'select-purpose' | 'confirm';

function parseLocation(raw: string): ParsedLocation | null {
  const parts = raw.split('_');
  // noUncheckedIndexedAccess: each parts[i] is string | undefined. Cache after
  // the length guard so the values are string-narrowed for the returned object.
  if (parts.length === 4) {
    const type = parts[0] ?? '';
    const area = parts[1] ?? '';
    const row = parts[2] ?? '';
    const colRaw = parts[3] ?? '';
    return { raw, type, area, row, col: parseInt(colRaw, 10), isReserved: false };
  }
  if (parts.length === 5 && parts[4] === 'R') {
    const type = parts[0] ?? '';
    const area = parts[1] ?? '';
    const row = parts[2] ?? '';
    const colRaw = parts[3] ?? '';
    return { raw, type, area, row, col: parseInt(colRaw, 10), isReserved: true };
  }
  return null;
}

function parseItemBarcode(barcode: string): string {
  let cleaned = barcode;
  if (cleaned.startsWith('LYB_')) {
    cleaned = cleaned.substring(4);
  }
  const splits = cleaned.split('_');
  if (splits[splits.length - 1] === '0000') {
    cleaned = splits.slice(0, -1).join('_');
  }
  return cleaned.trimEnd();
}

const PURPOSES: CheckOutPurpose[] = ['R&D', 'Production', 'Clinical', 'Reserved'];

export const ScanView: React.FC<ScanViewProps> = ({
  sheetIds,
  sheetNames,
  selectedSheetId,
  onSelectSheet,
  userName,
  onTransactionComplete,
}) => {
  const [step, setStep] = useState<ScanStep>('select-action');
  const [action, setAction] = useState<ExpoAmsAction | null>(null);
  const [location, setLocation] = useState<ParsedLocation | null>(null);
  const [logMap, setLogMap] = useState<LogMap>({});
  const [purpose, setPurpose] = useState<CheckOutPurpose | null>(null);
  const [scanning, setScanning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Opt-in: keep the sheet selected across repeated checkouts instead of the
  // default force-re-pick behavior. Off by default (most people work across
  // multiple sheets); plain useState so it resets to off on page refresh.
  const [keepSheetSelected, setKeepSheetSelected] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scanReaderRef = useRef<string>('expoams-qr-reader');
  const submittingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingFileCallbackRef = useRef<((text: string) => void) | null>(null);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current?.isScanning) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const startScanner = useCallback(async (onResult: (text: string) => void) => {
    setScanning(true);
    try {
      const scanner = new Html5Qrcode(scanReaderRef.current, {
        verbose: false,
      });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 15,
          qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
            const size = Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.75);
            return { width: size, height: size };
          },
        },
        (decodedText) => {
          scanner
            .stop()
            .then(() => {
              setScanning(false);
              onResult(decodedText);
            })
            .catch(() => setScanning(false));
        },
        () => {}, // ignore scan failures
      );
    } catch {
      setScanning(false);
      setError('Camera access failed. Please allow camera permission.');
    }
  }, []);

  const handlePhotoScan = useCallback((onResult: (text: string) => void) => {
    pendingFileCallbackRef.current = onResult;
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pendingFileCallbackRef.current) return;
    const callback = pendingFileCallbackRef.current;
    pendingFileCallbackRef.current = null;
    // Reset file input so same file can be selected again
    e.target.value = '';

    try {
      const scanner = new Html5Qrcode('expoams-file-scanner', { verbose: false });
      const result = await scanner.scanFileV2(file, false);
      scanner.clear();
      callback(result.decodedText);
    } catch {
      setError('Could not read barcode from photo. Try a clearer, well-lit image.');
    }
  }, []);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current?.isScanning) {
      await scannerRef.current.stop();
    }
    setScanning(false);
  }, []);

  const handleSelectAction = (selectedAction: 'Check In' | 'Check Out') => {
    if (!selectedSheetId) {
      setError('Please select an inventory sheet first.');
      return;
    }
    setAction(selectedAction);
    setStep('scan-location');
    setError(null);
    setSuccess(null);
  };

  const handleLocationScanned = (raw: string) => {
    const parsed = parseLocation(raw);
    if (!parsed) {
      setError(
        `Invalid location format: "${raw}". Expected TYPE_AREA_ROW_COL or TYPE_AREA_ROW_COL_R`,
      );
      return;
    }
    setLocation(parsed);
    // Auto-detect reserved from location suffix
    if (parsed.isReserved && action) {
      setAction(('Reserved ' + action) as ExpoAmsAction);
    }
    setStep('scan-items');
    setError(null);
  };

  const handleItemScanned = (raw: string) => {
    const identifier = parseItemBarcode(raw);
    setLogMap((prev) => {
      if (identifier in prev) return prev; // already added, don't reset
      return { ...prev, [identifier]: 0 };
    });
    setError(null);
  };

  const handleQuantityChange = (identifier: string, value: string) => {
    const qty = parseInt(value, 10);
    if (isNaN(qty) || qty < 0) return;
    if (qty === 0) {
      setLogMap((prev) => {
        const next = { ...prev };
        delete next[identifier];
        return next;
      });
    } else {
      setLogMap((prev) => ({ ...prev, [identifier]: qty }));
    }
  };

  const handleDone = () => {
    if (Object.keys(logMap).length === 0) {
      setError('No items scanned');
      return;
    }
    const hasZero = Object.entries(logMap).some(([, qty]) => qty <= 0);
    if (hasZero) {
      setError('Please enter a quantity for all items');
      return;
    }
    if (action === 'Check Out') {
      setStep('select-purpose');
    } else {
      setStep('confirm');
    }
  };

  const handlePurposeSelected = (p: CheckOutPurpose) => {
    setPurpose(p);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (!location || !action) return;
    if (!selectedSheetId) {
      setError('No inventory sheet selected. Please go back and pick one.');
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      let result: InventoryWriteResponse | undefined;
      switch (action) {
        case 'Check In':
          result = await expoamsClient.checkIn({
            sheetId: selectedSheetId,
            location: location.raw,
            logMap,
            name: userName,
          });
          break;
        case 'Check Out':
          result = await expoamsClient.checkOut({
            sheetId: selectedSheetId,
            location: location.raw,
            logMap,
            name: userName,
            usedFor: purpose!,
          });
          break;
        case 'Reserved Check In':
          result = await expoamsClient.checkInReserved({
            sheetId: selectedSheetId,
            location: location.raw,
            logMap,
            name: userName,
          });
          break;
        case 'Reserved Check Out':
          result = await expoamsClient.checkOutReserved({
            sheetId: selectedSheetId,
            location: location.raw,
            logMap,
            name: userName,
          });
          break;
      }
      setSuccess(result?.message || 'Transaction completed');
      if (result?.success !== false) onTransactionComplete?.(selectedSheetId);
      // Keep submitting guard active until full state reset to prevent
      // accidental re-taps during the success message display window
      setTimeout(() => {
        setStep('select-action');
        setAction(null);
        setLocation(null);
        setLogMap({});
        setPurpose(null);
        setSuccess(null);
        setSubmitting(false);
        submittingRef.current = false;
        // Force re-pick before the next transaction by default, so users
        // don't accidentally chain operations against a previously-selected
        // (possibly wrong) sheet — unless they've opted into keeping the
        // sheet selected (bulk single-sheet workflows).
        // Failure path intentionally does NOT clear: user retrying a
        // failed transaction should keep their sheet context.
        if (!keepSheetSelected) {
          onSelectSheet('');
        }
      }, 2000);
    } catch (err: unknown) {
      let message: string;
      if (err instanceof InsufficientPrivilegeError) {
        message = "You don't have permission to do this.";
      } else if (err instanceof InvalidRequestError) {
        message = err.message;
      } else {
        message = 'Transaction failed';
      }
      setError(message);
      // Error is intentionally not cleared here — it persists until the
      // user's next action (each handler above already calls setError(null)
      // at its start), rather than disappearing on a fixed timer.
      setTimeout(() => {
        setStep('select-action');
        setAction(null);
        setLocation(null);
        setLogMap({});
        setPurpose(null);
        setSubmitting(false);
        submittingRef.current = false;
      }, 4000);
    }
  };

  const handleBack = () => {
    void stopScanner();
    switch (step) {
      case 'scan-location':
        setStep('select-action');
        setAction(null);
        break;
      case 'scan-items':
        setStep('scan-location');
        setLogMap({});
        break;
      case 'select-purpose':
        setStep('scan-items');
        break;
      case 'confirm':
        setStep(action === 'Check Out' ? 'select-purpose' : 'scan-items');
        break;
      default:
        break;
    }
    setError(null);
  };

  // ---- Render ----

  return (
    <div>
      {/* QR Scanner container (hidden when not scanning) */}
      <div
        id={scanReaderRef.current}
        style={{ display: scanning ? 'block' : 'none', marginBottom: '1rem' }}
      />
      {/* Hidden elements for file-based scanning */}
      <div id="expoams-file-scanner" style={{ display: 'none' }} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => void handleFileSelected(e)}
      />

      {error && <div className="expoams-error">{error}</div>}
      {success && <div className="expoams-success">{success}</div>}

      {/* Step: Select Action */}
      {step === 'select-action' && (
        <>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontSize: '0.85rem', color: '#666' }}>Select Inventory</label>
            <select
              className="expoams-sheet-select"
              value={selectedSheetId}
              onChange={(e) => onSelectSheet(e.target.value)}
            >
              <option value="" disabled>
                — Select a sheet —
              </option>
              {sheetIds.map((id) => (
                <option key={id} value={id}>
                  {sheetNames[id] || id}
                </option>
              ))}
            </select>
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              marginBottom: '1rem',
              fontSize: '0.85rem',
              color: '#666',
            }}
          >
            <input
              type="checkbox"
              checked={keepSheetSelected}
              onChange={(e) => setKeepSheetSelected(e.target.checked)}
            />
            Keep this sheet selected after checkout
          </label>

          {!selectedSheetId && (
            <div
              style={{
                textAlign: 'center',
                color: '#888',
                margin: '1rem 0',
                fontSize: '0.9rem',
              }}
            >
              Pick an inventory sheet above to begin.
            </div>
          )}

          <div className="expoams-action-grid">
            <button
              className="expoams-action-btn check-in"
              onClick={() => handleSelectAction('Check In')}
              disabled={!selectedSheetId}
            >
              CHECK IN
            </button>
            <button
              className="expoams-action-btn check-out"
              onClick={() => handleSelectAction('Check Out')}
              disabled={!selectedSheetId}
            >
              CHECK OUT
            </button>
          </div>
        </>
      )}

      {/* Step: Scan Location */}
      {step === 'scan-location' && (
        <>
          <div className="expoams-header">
            <button className="expoams-back-btn" onClick={handleBack}>
              ← Back
            </button>
            <h2>{action}</h2>
            <div className="expoams-header-sub">{sheetNames[selectedSheetId]}</div>
          </div>
          {!scanning && (
            <>
              <button
                className="expoams-scan-btn"
                onClick={() => void startScanner(handleLocationScanned)}
              >
                📷 Scan Location QR Code
              </button>
              <button
                className="expoams-scan-btn secondary"
                onClick={() => handlePhotoScan(handleLocationScanned)}
              >
                🖼 Scan from Photo
              </button>
            </>
          )}
          {/* Manual entry fallback */}
          {!scanning && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const input = (e.target as HTMLFormElement).elements.namedItem(
                  'manualLocation',
                ) as HTMLInputElement;
                if (input.value.trim()) {
                  handleLocationScanned(input.value.trim());
                  input.value = '';
                }
              }}
              style={{ marginTop: '0.75rem' }}
            >
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  name="manualLocation"
                  className="expoams-add-input"
                  placeholder="Or type location (e.g. RACK_A_1_1)"
                  style={{ flex: 1 }}
                />
                <button type="submit" className="expoams-add-btn">
                  Go
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {/* Step: Scan Items */}
      {step === 'scan-items' && (
        <>
          <div className="expoams-header">
            <button className="expoams-back-btn" onClick={handleBack}>
              ← Back
            </button>
            <h2>{action}</h2>
            <div className="expoams-header-sub">{sheetNames[selectedSheetId]}</div>
          </div>

          {location && <div className="expoams-location-badge">📍 {location.raw}</div>}

          {!scanning && (
            <>
              <button
                className="expoams-scan-btn"
                onClick={() => void startScanner(handleItemScanned)}
              >
                📷 Scan Item
              </button>
              <button
                className="expoams-scan-btn secondary"
                onClick={() => handlePhotoScan(handleItemScanned)}
              >
                🖼 Scan from Photo
              </button>
            </>
          )}
          {/* Manual entry fallback */}
          {!scanning && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const input = (e.target as HTMLFormElement).elements.namedItem(
                  'manualItem',
                ) as HTMLInputElement;
                if (input.value.trim()) {
                  handleItemScanned(input.value.trim());
                  input.value = '';
                }
              }}
              style={{ marginTop: '-0.5rem', marginBottom: '0.75rem' }}
            >
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  name="manualItem"
                  className="expoams-add-input"
                  placeholder="Or type item code"
                  style={{ flex: 1 }}
                />
                <button type="submit" className="expoams-add-btn">
                  Add
                </button>
              </div>
            </form>
          )}

          <div className="expoams-item-list">
            {Object.entries(logMap).map(([identifier, qty]) => (
              <div key={identifier} className="expoams-item-row">
                <span className="expoams-item-name">{identifier}</span>
                <input
                  className="expoams-qty-input"
                  type="number"
                  inputMode="numeric"
                  placeholder="Qty"
                  value={qty || ''}
                  onChange={(e) => handleQuantityChange(identifier, e.target.value)}
                />
              </div>
            ))}
          </div>

          {Object.keys(logMap).length > 0 && (
            <div className="expoams-bottom-action">
              <button className="expoams-confirm-btn" onClick={handleDone}>
                Done
              </button>
            </div>
          )}
        </>
      )}

      {/* Step: Select Purpose (regular check-out only) */}
      {step === 'select-purpose' && (
        <>
          <div className="expoams-header">
            <button className="expoams-back-btn" onClick={handleBack}>
              ← Back
            </button>
            <h2>Select Purpose</h2>
          </div>
          <div className="expoams-purpose-list">
            {PURPOSES.map((p) => (
              <button
                key={p}
                className="expoams-purpose-btn"
                onClick={() => handlePurposeSelected(p)}
              >
                {p}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Step: Confirm */}
      {step === 'confirm' && location && (
        <>
          <div className="expoams-header">
            <button className="expoams-back-btn" onClick={handleBack}>
              ← Back
            </button>
            <h2>Confirm {action}</h2>
            <div className="expoams-header-sub">{sheetNames[selectedSheetId]}</div>
          </div>

          <div className="expoams-confirm-section">
            <div className="expoams-confirm-label">Location</div>
            <div className="expoams-confirm-value">📍 {location.raw}</div>
          </div>

          {purpose && (
            <div className="expoams-confirm-section">
              <div className="expoams-confirm-label">Purpose</div>
              <div className="expoams-confirm-value">{purpose}</div>
            </div>
          )}

          <div className="expoams-confirm-section">
            <div className="expoams-confirm-label">Items</div>
            {Object.entries(logMap).map(([identifier, qty]) => (
              <div
                key={identifier}
                style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}
              >
                <span>{identifier}</span>
                <span style={{ fontWeight: 600 }}>x{qty}</span>
              </div>
            ))}
          </div>

          <div className="expoams-confirm-section">
            <div className="expoams-confirm-label">Signed as</div>
            <div className="expoams-confirm-value">{userName}</div>
          </div>

          <button
            className="expoams-confirm-btn"
            onClick={() => void handleConfirm()}
            disabled={submitting}
          >
            {submitting ? 'Processing...' : 'Confirm'}
          </button>
        </>
      )}
    </div>
  );
};

export default ScanView;
